import React, { useState, useEffect } from "react";
import style from "./style.module.scss";

import Winston from "./assets/Winston.svg";
import GPTZero from "./assets/GPTZero.svg";
import ZeroGPT from "./assets/ZeroGPT.svg";
import Originality from "./assets/Originality.svg";
import { ReactComponent as CheckIcon } from "./assets/CheckIcon.svg";
import { ReactComponent as AiDetectedIcon } from "./assets/AiDetected.svg";
import { ReactComponent as UncertainIcon } from "./assets/Uncertain.svg";
import { ReactComponent as LoaderIcon } from "./assets/Loader.svg";
import { HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

type ServiceName =
	"WINSTON" | "ORIGINALITY" | "GPT_ZERO" | "ZERO_GPT" | "OPEN_AI" | "INTERNAL_UNIQUE";

interface DataItem {
	serviceName: ServiceName;
	text: number;
	isError: boolean;
}

type Props = {
	loading?: boolean;
	bottomAiResultBadge?: boolean;
	data?: DataItem[];
	isInput?: boolean; 
	isTrial?: boolean; 
};

const serviceOrder: ServiceName[] = [
	"WINSTON",
	"ORIGINALITY",
	"GPT_ZERO",
	"ZERO_GPT",
];

const imgs: Record<ServiceName, string> = {
	WINSTON: Winston,
	ORIGINALITY: Originality,
	GPT_ZERO: GPTZero,
	ZERO_GPT: ZeroGPT,
	OPEN_AI: "",
	INTERNAL_UNIQUE: "",
};

const getUniquenessBarColor = (uniquenessPercent: number) => {
	if (uniquenessPercent < 25) {
		return "rgba(242, 26, 52, 1)";
	}
	else if (uniquenessPercent <= 50) {
		return "rgba(253, 137, 7, 1)";
	}
	else {
		return "rgba(0, 172, 59, 1)";
	}
};

const calculateUniquenessPercent = (serviceName: ServiceName, score: number): number => {
	let result: number;
	
	if (serviceName === 'WINSTON' || serviceName === 'ORIGINALITY') {
		result = Math.round((1- score) * 100);
	} else {
		result = Math.round((1 - score) * 100);
	}
	
	console.log(`[calculateUniquenessPercent] ${serviceName}: score=${score} → uniqueness=${result}%`);
	
	return result;
};

export const TextDetectionResult: React.FC<Props> = ({
	loading = false,
	bottomAiResultBadge = true,
	data = [],
	isInput = false,
	isTrial = false,
}) => {
	const { t } = useTranslation();
	const [fakePercentages, setFakePercentages] = useState<Partial<Record<ServiceName, number>>>({});

	useEffect(() => {
		if (isTrial && data && data.length > 0) {
			const newFakePercentages: Partial<Record<ServiceName, number>> = {};
			data.forEach((item) => {
				if (item.isError) {
					newFakePercentages[item.serviceName] =
						Math.floor(Math.random() * (35 - 15 + 1)) + 15;
				}
			});
			setFakePercentages(newFakePercentages);
		}
	}, [isTrial, data]);

	const renderItems = () => {
		const sortedData = serviceOrder
			.map((serviceName) => data.find((item) => item.serviceName === serviceName))
			.filter((item): item is DataItem => item !== undefined);

		return (
			<div className={style.TextDetectionResult}>
				<div className={style.TextDetectionResult__flexWrapper}>
					{sortedData.map((el: DataItem) => (
						<div
							key={el.serviceName}
							className={style.TextDetectionResult__indicatorContainer}
						>
							<div
								className={`${style.TextDetectionResult__titleContainer} ${
									el.serviceName === "ORIGINALITY" ? style.originalityContainer : ""
								}`}
							>
								<img
									className={style.TextDetectionResult__titleContainer_img}
									src={imgs[el.serviceName]}
									alt={el.serviceName}
								/>
								{loading ? (
									<LoaderIcon className={style.LoaderAnimation} />
								) : (
									<small
										className={
											!el.isError && el.text < 0
												? style["upgrade-text"]
												: el.isError && !(isTrial && fakePercentages[el.serviceName])
												? style["error-text"]
												: ""
										}
									>
										{!el.isError && el.text >= 0 && (
											<div className={style.uniqueTextContainer}>
												<span>{calculateUniquenessPercent(el.serviceName, el.text)}%</span>
												<span style={{ marginLeft: "4px", fontWeight: "bold" }}>
													Unique
												</span>
												{((!isInput && calculateUniquenessPercent(el.serviceName, el.text) < 50) ||
													(isInput &&
														calculateUniquenessPercent(el.serviceName, el.text) >= 25 &&
														calculateUniquenessPercent(el.serviceName, el.text) < 50)) && (
													<div className={style.infoIconContainer}>
														<HelpCircle size={14} className={style.infoIcon} />
														<div className={style.tooltip}>
															{t("detection.inconsistentResults")}
														</div>
													</div>
												)}
											</div>
										)}
										{!el.isError && el.text < 0 && t("detection.paidPlan")}
										{el.isError && isTrial && fakePercentages[el.serviceName] && (
											<div className={style.uniqueTextContainer}>
												<span>{fakePercentages[el.serviceName]}%</span>
												<span style={{ marginLeft: "4px", fontWeight: "bold" }}>
													Unique
												</span>
											</div>
										)}
										{el.isError &&
											!(isTrial && fakePercentages[el.serviceName]) &&
											t("detection.error")}
									</small>
								)}
							</div>
							<div
								className={`${style.TextDetectionResult__indicator} ${
									(!el.isError && el.text >= 0 && calculateUniquenessPercent(el.serviceName, el.text) > 70) ||
									(isTrial &&
										fakePercentages[el.serviceName] !== undefined &&
										fakePercentages[el.serviceName]! > 70)
										? style["green-state"]
										: ""
								} ${
									isInput &&
									!el.isError &&
									el.text >= 0 &&
									calculateUniquenessPercent(el.serviceName, el.text) === 0
										? style["ai-detected"]
										: ""
								} ${
									isInput &&
									!el.isError &&
									el.text >= 0 &&
									calculateUniquenessPercent(el.serviceName, el.text) < 25
										? style["ai-detected"]
										: ""
								}`}
							>
								<div
									style={{
										width: `${
											loading
												? 0
												: isTrial && fakePercentages[el.serviceName]
												? fakePercentages[el.serviceName]
												: !el.isError && el.text >= 0
												? calculateUniquenessPercent(el.serviceName, el.text)
												: 100
										}%`,
										backgroundColor:
											el.text < 0
												? "grey"
												: isTrial && fakePercentages[el.serviceName] !== undefined
												? getUniquenessBarColor(fakePercentages[el.serviceName]!)
												: isInput
												? getUniquenessBarColor(calculateUniquenessPercent(el.serviceName, el.text))
												: !el.isError &&
												  el.text >= 0 &&
												  calculateUniquenessPercent(el.serviceName, el.text) < 25
												? "rgba(253, 137, 7, 1)"
												: getUniquenessBarColor(calculateUniquenessPercent(el.serviceName, el.text)),
										backgroundImage:
											el.text < 0
												? "linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)"
												: "none",
										backgroundSize: "40px 40px",
									}}
									className={`${style.TextDetectionResult__indicator_greenBar} ${
										!el.isError && el.text >= 0 && calculateUniquenessPercent(el.serviceName, el.text) >= 50
											? style["high-uniqueness"]
											: ""
									}`}
								/>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderResultIcon = () => {
		if (data && !loading) {
			const activeResults = data.filter((result: any) => result.text >= 0 && !result.isError);

			if (activeResults.length === 0) {
				console.warn("No valid results to display");
				return <></>;
			}

			const resultSum = activeResults.reduce((sum: number, cur: any) => {
				const value = parseFloat(cur.text);
				return isNaN(value) ? sum : sum + value;
			}, 0);

			const resultAverage = Math.round((resultSum / activeResults.length) * 100);

			if (resultAverage > 75) {
				return (
					<div
						style={{
							position: "absolute",
							top: "-35px",
							left: "0px",
							width: "100%",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div className={style.ResultIconContainer}>
							<div className={style.ResultIconContainer__indicator}>
								<AiDetectedIcon />
							</div>
							<small>{t("detection.detected")}</small>
						</div>
						{isInput && (
							<div className={style.trustUsContainer}>
								<small className={style.trustUsText}>{t("detection.whyTrustUs")}</small>
								<div className={style.trustTooltip}>{t("detection.trustExplanation")}</div>
							</div>
						)}
					</div>
				);
			}

			if (resultAverage === 50) {
				return (
					<div
						style={{
							position: "absolute",
							top: "-35px",
							left: "0px",
						}}
						className={style.ResultIconContainer}
					>
						<div className={style.ResultIconContainer__indicator}>
							<UncertainIcon />
						</div>
						<small>{t("detection.uncertain")}</small>
					</div>
				);
			}

			if (resultAverage <= 75 && resultAverage > 40) {
				return (
					<div
						style={{
							position: "absolute",
							top: "-35px",
							left: "0px",
						}}
						className={style.ResultIconContainer}
					>
						<div className={style.ResultIconContainer__indicator}>
							<UncertainIcon />
						</div>
						<small>{t("detection.uncertain")}</small>
					</div>
				);
			}

			if (resultAverage <= 40) {
				return (
					<div
						style={{
							position: "absolute",
							top: "-35px",
							left: "0px",
						}}
						className={style.ResultIconContainer}
					>
						<div className={style.ResultIconContainer__indicator_green}>
							<CheckIcon />
						</div>
						<small>{t("detection.human")}</small>
					</div>
				);
			}
		}

		return <></>;
	};

	return (
		<div className={style.Container}>
			{data && !loading ? (
				renderItems()
			) : (
				<div className={style.TextDetectionResult}>
					<div className={style.TextDetectionResult__flexWrapper}>
						<div className={style.TextDetectionResult__indicatorContainer}>
							<div className={style.TextDetectionResult__titleContainer}>
								{loading ? (
									<LoaderIcon className={style.LoaderAnimation} />
								) : (
									<small></small>
								)}
							</div>
							<div className={style.TextDetectionResult__indicator}>
								<div
									style={{ width: `${loading ? 0 : 0}%` }}
									className={style.TextDetectionResult__indicator_greenBar}
								/>
							</div>
						</div>
						<div className={style.TextDetectionResult__indicatorContainer}>
							<div className={style.TextDetectionResult__titleContainer}>
								{loading ? (
									<LoaderIcon className={style.LoaderAnimation} />
								) : (
									<small>{0}%</small>
								)}
							</div>
							<div className={style.TextDetectionResult__indicator}>
								<div
									style={{ width: `${loading ? 0 : 0}%` }}
									className={style.TextDetectionResult__indicator_greenBar}
								/>
							</div>
						</div>
					</div>
					<div className={style.TextDetectionResult__flexWrapper}>
						<div className={style.TextDetectionResult__indicatorContainer}>
							<div className={style.TextDetectionResult__titleContainer}>
								{loading ? (
									<LoaderIcon className={style.LoaderAnimation} />
								) : (
									<small></small>
								)}
							</div>
							<div className={style.TextDetectionResult__indicator}>
								<div
									style={{ width: `${loading ? 0 : 0}%` }}
									className={style.TextDetectionResult__indicator_greenBar}
								/>
							</div>
						</div>
						<div className={style.TextDetectionResult__indicatorContainer}>
							<div className={style.TextDetectionResult__titleContainer}>
								{loading ? (
									<LoaderIcon className={style.LoaderAnimation} />
								) : (
									<small></small>
								)}
							</div>
							<div className={style.TextDetectionResult__indicator}>
								<div
									style={{ width: `${loading ? 0 : 0}%` }}
									className={style.TextDetectionResult__indicator_greenBar}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
			{loading ? (
				<div className={style.ResultContainer}>
					<div
						style={{
							position: "absolute",
							top: "-35px",
							left: "0px",
							width: "100%",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div className={style.ResultIconContainer}>
							<div className={style.ResultIconContainer__indicator}>
								<LoaderIcon className={style.LoaderAnimation} />
							</div>
							<small>Scanning...</small>
						</div>
						{isInput && (
							<div className={style.trustUsContainer}>
								<small className={style.trustUsText}>{t("detection.whyTrustUs")}</small>
								<div className={style.trustTooltip}>{t("detection.trustExplanation")}</div>
							</div>
						)}
					</div>
				</div>
			) : data ? (
				<div className={style.ResultContainer}>
					<div className={style.ResultIconContainer}>
						{bottomAiResultBadge ? (
							<>
								<div className={style.ResultIconContainer__indicator}>
									<CheckIcon />
								</div>
								<small>{t("detection.human")}</small>
							</>
						) : (
							renderResultIcon()
						)}
					</div>
				</div>
			) : (
				<></>
			)}
		</div>
	);
};